import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Wallet, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardHeader, AddButton, CollapsibleContent, Toast, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useFinancialCalculations } from '../../hooks/useFinancialCalculations';
import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency } from '../../utils/formatters';

interface BudgetWidgetProps {
  onAdd: () => void;
  variant?: 'card' | 'page';
}

export const BudgetWidget: React.FC<BudgetWidgetProps> = ({ onAdd, variant = 'card' }) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { updateBudgetCategory, handleDeleteBudgetCategory, totalBudgeted, totalSpent, handleBatchDelete } = useFinancialData();
  const { budgetUtilization } = useFinancialCalculations();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit } = useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('budget');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const handleDeleteAsync = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteBudgetCategory(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Budget', indices, sheetId);
  };

  const editMode = useEditMode(handleDeleteAsync, budgetUtilization.length, handleBatchDeleteAsync);
  const { exitEditMode } = editMode;

  useEffect(() => {
    if (collapsed) exitEditMode();
  }, [collapsed, exitEditMode]);

  const handleSave = async (index: number, field: 'budgeted' | 'spent') => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateBudgetCategory(index, field, value, sheetId);

      const cat = budgetUtilization[index];
      const newBudgeted = field === 'budgeted' ? value : cat.budgeted;
      const newSpent = field === 'spent' ? value : cat.spent;
      if (newSpent > newBudgeted && newBudgeted > 0) {
        const overBy = newSpent - newBudgeted;
        setToastMessage(`Heads up: ${cat.category} is over budget by ${formatCurrency(overBy)}`);
      }
    }
  };

  const dismissToast = useCallback(() => setToastMessage(null), []);

  const handleKeyDown = (e: React.KeyboardEvent, onSave: () => void) => {
    if (e.key === 'Enter') onSave();
    if (e.key === 'Escape') cancelEdit();
  };

  const toggleGroup = (group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  // Build ordered rows: grouped items get a header row, ungrouped stay flat
  const { rows } = useMemo(() => {
    type Row =
      | { type: 'flat'; idx: number }
      | { type: 'group-header'; group: string; totalBudgeted: number; totalSpent: number; count: number }
      | { type: 'group-item'; idx: number; group: string };

    // Collect groups in order of first appearance
    const groupOrder: string[] = [];
    const groupItems = new Map<string, number[]>();
    const ungrouped: number[] = [];

    budgetUtilization.forEach((cat, idx) => {
      const g = (cat as any).group || '';
      if (g) {
        if (!groupItems.has(g)) {
          groupItems.set(g, []);
          groupOrder.push(g);
        }
        groupItems.get(g)!.push(idx);
      } else {
        ungrouped.push(idx);
      }
    });

    const result: Row[] = [];

    // Ungrouped first
    ungrouped.forEach(idx => result.push({ type: 'flat', idx }));

    // Then each group
    groupOrder.forEach(group => {
      const items = groupItems.get(group)!;
      const totalBudgeted = items.reduce((s, i) => s + budgetUtilization[i].budgeted, 0);
      const totalSpent = items.reduce((s, i) => s + budgetUtilization[i].spent, 0);
      result.push({ type: 'group-header', group, totalBudgeted, totalSpent, count: items.length });
      items.forEach(idx => result.push({ type: 'group-item', idx, group }));
    });

    return { rows: result, hasGroups: groupOrder.length > 0 };
  }, [budgetUtilization]);

  const remaining = totalBudgeted - totalSpent;
  const isPage = variant === 'page';

  const renderEditableCell = (idx: number, field: 'budgeted' | 'spent', value: number) => {
    if (isEditing('budgetCategory', idx, field)) {
      return (
        <input
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, () => handleSave(idx, field))}
          onBlur={() => handleSave(idx, field)}
          onFocus={e => e.target.select()}
          className={`w-20 px-2 py-0.5 text-xs rounded border ${
            darkMode
              ? 'bg-slate-800 border-slate-600 text-white'
              : 'bg-white border-slate-200 text-slate-800'
          } focus:outline-none focus:ring-1 focus:ring-purple-500 text-right`}
          autoFocus
        />
      );
    }
    return (
      <span
        onClick={() => startEdit('budgetCategory', idx, field, value)}
        className={`cursor-pointer hover:bg-white/10 rounded ${darkMode ? 'text-slate-300' : 'text-slate-800'}`}
      >
        {formatCurrency(value, privacyMode)}
      </span>
    );
  };

  const renderCategoryRow = (idx: number, indented: boolean) => {
    const cat = budgetUtilization[idx];
    const available = cat.budgeted - cat.spent;
    return (
      <tr
        key={idx}
        className={`${darkMode ? 'text-slate-300' : 'text-slate-800'} border-b ${darkMode ? 'border-white/10' : 'border-slate-100'}`}
      >
        {editMode.isEditMode && (
          <td className="py-2 pr-2">
            <EditModeCheckbox
              checked={editMode.selectedIndices.has(idx)}
              onToggle={() => editMode.toggleIndex(idx)}
            />
          </td>
        )}
        <td className={`py-2 font-${indented ? 'medium' : 'semibold'} whitespace-nowrap ${indented ? 'pl-8' : ''}`}>
          <div className="flex items-center gap-2">
            <div className={`${indented ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full flex-shrink-0`} style={{ backgroundColor: cat.color }} />
            {cat.category}
          </div>
        </td>
        <td className="text-right whitespace-nowrap">{renderEditableCell(idx, 'budgeted', cat.budgeted)}</td>
        <td className="text-right whitespace-nowrap">{renderEditableCell(idx, 'spent', cat.spent)}</td>
        <td className={`text-right font-semibold whitespace-nowrap ${available >= 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-red-400' : 'text-red-600')}`}>
          {formatCurrency(available, privacyMode)}
        </td>
      </tr>
    );
  };

  return (
    <Card variant={variant}>
      <CardHeader
        title="Monthly Budget"
        icon={<Wallet size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={budgetUtilization.length}
            />
            <AddButton onClick={onAdd} />
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('budget')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
      {editMode.isEditMode && (
        <EditModeActionBar
          selectedCount={editMode.selectedIndices.size}
          totalCount={budgetUtilization.length}
          allSelected={editMode.selectedIndices.size === budgetUtilization.length}
          onSelectAll={() => editMode.selectAll(budgetUtilization.length)}
          onDeselectAll={editMode.deselectAll}
          onDeleteSelected={editMode.deleteSelected}
          isDeleting={editMode.isDeleting}
        />
      )}

      {budgetUtilization.length === 0 ? (
        <div className={`text-center py-6 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          <p className="text-sm">No budget categories yet</p>
          <p className="text-xs mt-1">Add categories to start tracking</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed" style={{ minWidth: 480 }}>
            <thead>
              <tr className={`${darkMode ? 'text-slate-400' : 'text-slate-600'} border-b ${darkMode ? 'border-white/10' : 'border-slate-200'}`}>
                {editMode.isEditMode && <th className="w-8 pb-2" />}
                <th className="text-left pb-2 pl-4 whitespace-nowrap w-1/4">Category</th>
                <th className="text-right pb-2 whitespace-nowrap w-1/4">Budgeted</th>
                <th className="text-right pb-2 whitespace-nowrap w-1/4">Spent</th>
                <th className="text-right pb-2 whitespace-nowrap w-1/4">Available</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => {
                if (row.type === 'group-header') {
                  const isGroupCollapsed = collapsedGroups.has(row.group);
                  const groupAvailable = row.totalBudgeted - row.totalSpent;
                  return (
                    <tr
                      key={`gh-${row.group}`}
                      className={`${darkMode ? 'bg-white/[0.03]' : 'bg-slate-50/80'} cursor-pointer`}
                      onClick={() => toggleGroup(row.group)}
                    >
                      {editMode.isEditMode && <td />}
                      <td className={`py-2 pl-2 font-semibold border-l-2 ${darkMode ? 'border-violet-500/40 text-slate-300' : 'border-violet-400/50 text-slate-700'}`}>
                        <div className="flex items-center gap-1.5">
                          {isGroupCollapsed
                            ? <ChevronRight size={14} className={darkMode ? 'text-slate-500' : 'text-slate-400'} />
                            : <ChevronDown size={14} className={darkMode ? 'text-slate-500' : 'text-slate-400'} />
                          }
                          {row.group}
                          <span className={`text-xs font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            ({row.count})
                          </span>
                        </div>
                      </td>
                      <td className={`text-right py-2 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {formatCurrency(row.totalBudgeted, privacyMode)}
                      </td>
                      <td className={`text-right py-2 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {formatCurrency(row.totalSpent, privacyMode)}
                      </td>
                      <td className={`text-right py-2 font-semibold ${groupAvailable >= 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-red-400' : 'text-red-600')}`}>
                        {formatCurrency(groupAvailable, privacyMode)}
                      </td>
                    </tr>
                  );
                }

                if (row.type === 'group-item') {
                  if (collapsedGroups.has(row.group)) return null;
                  return renderCategoryRow(row.idx, true);
                }

                return renderCategoryRow(row.idx, false);
              })}
            </tbody>
            <tfoot>
              <tr className={`font-bold border-t ${darkMode ? 'border-white/10 text-white' : 'border-slate-200 text-slate-800'}`}>
                {editMode.isEditMode && <td />}
                <td className="pt-3 pl-4 whitespace-nowrap">Total</td>
                <td className="text-right pt-3 whitespace-nowrap">{formatCurrency(totalBudgeted, privacyMode)}</td>
                <td className="text-right pt-3 whitespace-nowrap">{formatCurrency(totalSpent, privacyMode)}</td>
                <td className={`text-right pt-3 whitespace-nowrap ${remaining >= 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-red-400' : 'text-red-600')}`}>
                  {formatCurrency(remaining, privacyMode)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      </CollapsibleContent>

      {toastMessage && (
        <Toast message={toastMessage} variant="warning" duration={4000} onDismiss={dismissToast} />
      )}
    </Card>
  );
};
